import { useState } from "react";
import { Text } from "@/components/ui/text";
import { QRCode } from "@/components/ui/qr-code";
import { Button } from "@/components/ui/button";
import { Copy, Check } from "lucide-react";
import type { Dictionary } from "../../../../dictionaries";
import {
  StatusOverlay,
  SectionLabel,
  InfoCard,
  InfoRow,
} from "./status-overlay";

interface WaitingQRViewProps {
  qrCode: string;
  paymentToken?: string;
  dictionary: Dictionary;
  onCancel: () => void | Promise<void>;
}

export function WaitingQRView({
  qrCode,
  paymentToken,
  dictionary,
  onCancel,
}: WaitingQRViewProps) {
  const { waiting } = dictionary;
  const [copied, setCopied] = useState(false);

  const copyPaymentToken = async () => {
    if (!paymentToken) return;

    try {
      await navigator.clipboard.writeText(paymentToken);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {

    }
  };

  return (
    <StatusOverlay
      icon={
        <div className="rounded-lg border border-border bg-background p-3">
          <QRCode data={qrCode} className="size-56" robustness="H" />
        </div>
      }
      title={waiting.qr.title}
      description={waiting.qr.description}
      cancelLabel={waiting.cancel}
      onCancel={onCancel}
    >
      {/* Payment token */}
      {paymentToken && (
        <div className="space-y-3">
          <SectionLabel>{waiting.qr.paymentToken}</SectionLabel>
          <InfoCard>
            <InfoRow className="flex items-center justify-between">
              <Text className="font-mono text-xl font-bold">
                {paymentToken}
              </Text>
              <Button
                variant="ghost"
                size="icon"
                className="size-8 shrink-0"
                onClick={copyPaymentToken}
              >
                {copied ? (
                  <Check className="size-4 text-green-500" />
                ) : (
                  <Copy className="size-4 text-muted-foreground" />
                )}
              </Button>
            </InfoRow>
          </InfoCard>
        </div>
      )}

      {/* How to pay steps */}
      <div className="space-y-3">
        <SectionLabel>{waiting.qr.howToPay}</SectionLabel>
        <InfoCard>
          <InfoRow>
            <ol className="list-inside list-decimal space-y-1.5 text-sm text-muted-foreground">
              <li>{waiting.qr.steps["1"]}</li>
              <li>{waiting.qr.steps["2"]}</li>
              <li>{waiting.qr.steps["3"]}</li>
              <li>{waiting.qr.steps["4"]}</li>
              <li>{waiting.qr.steps["5"]}</li>
              <li>{waiting.qr.steps["6"]}</li>
            </ol>
          </InfoRow>
          <InfoRow border className="text-xs text-muted-foreground">
            {waiting.qr.instant}
          </InfoRow>
        </InfoCard>
      </div>
    </StatusOverlay>
  );
}
